//Built in Libraries
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router';

//Third Part Libraries
import { Transition, Menu } from '@headlessui/react';
import { BellIcon } from '@heroicons/react/solid';
import { getElapsedTime } from '../../../../utils';
import { useDispatch, useSelector } from 'react-redux';
import { setNotificationId } from '../../../../store/Notification';
import { useNotifications } from '../../../../hooks/ReactQueryHooks/useNotificationQuery';
import { accountSelector, authSelector } from '../../../../store';
import styles from './Notifications.module.css';
import {
  postReadNotification,
  postReadAllNotifications,
} from '../../../../api/Notification';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IGetNotificationsServiceResData,
  IPostReadAllNotificationPayload,
  IPostReadNotificationService,
} from '../../../../api/Notification/types';
import { SparklesIcon } from '@heroicons/react/outline';
import Error from '../../../UI/Error';
import { getNotificationRoutes } from '@stptech/shared-kit/build/notificationRoutes';

//Components

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function Notifications() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: currentAccountInfo } = useSelector(accountSelector);
  const { data: currentAccount } = useSelector(authSelector);
  const [perPage, setPerPage] = useState<number>(5);
  const [notificationNav, setNotificationNav] = useState<string>('unread');
  const queryClient = useQueryClient();

  const updateReadNotification = useMutation(
    (data: IPostReadNotificationService) => postReadNotification(data),
    {
      onSuccess: () => queryClient.invalidateQueries(['get-notifications']),
    }
  );

  const updateReadNotificationAll = useMutation(
    (data: IPostReadAllNotificationPayload) =>
      postReadAllNotifications({ data }),
    {
      onSuccess: () => queryClient.invalidateQueries(['get-notifications']),
    }
  );

  const notificationOpenHandler = (
    notification: IGetNotificationsServiceResData
  ) => {
    const resourcePath = getNotificationRoutes(notification);

    updateReadNotification.mutate({ notificationId: notification.uuid });

    navigate(resourcePath, {
      replace: true,
    });
    if (!notification.isRead) {
      dispatch(setNotificationId(notification.uuid));
    }
  };

  const fetchNotifications = useNotifications({
    params: { user_id: currentAccount.id, type: 'IN_APP' },
  });
  const notifications = fetchNotifications?.data?.data?.filter(
    (ntf: IGetNotificationsServiceResData) =>
      notificationNav === 'read' ? ntf.isRead : !ntf.isRead
  );
  const notificationsUnreadCount = fetchNotifications?.data?.unReadCount;
  return (
    <>
      <div className={styles.container}>
        <Menu as='div' className=''>
          <Menu.Button className={styles.wrapper} onClick={() => setPerPage(5)}>
            <span className='sr-only'>Open user menu</span>
            {Number(notificationsUnreadCount) > 0 && (
              <span className={styles['unread-wrapper']}>
                {notificationsUnreadCount}
              </span>
            )}
            <BellIcon className='h-5 w-5' />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter='transition ease-out duration-100'
            enterFrom='transform opacity-0 scale-95'
            enterTo='transform opacity-100 scale-100'
            leave='transition ease-in duration-75'
            leaveFrom='transform opacity-100 scale-100'
            leaveTo='transform opacity-0 scale-95'
          >
            <Menu.Items
              className={`absolute flex flex-col items-center space-y-2 w-full right-0 md:right-10 top-14 h-screen/80 ${
                fetchNotifications.isSuccess
                  ? 'overflow-y-auto'
                  : 'overflow-hidden'
              } rounded-md shadow-lg p-5 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none`}
            >
              <div className='sticky bg-white w-full'>
                <h2 className='mt-0'>Notifications</h2>
                <div className='border-b-2 pb-2 flex justify-between'>
                  <nav className='space-x-4'>
                    <span
                      className={`cursor-pointer ${
                        notificationNav === 'unread' && styles['active-nav']
                      }`}
                      onClick={() => setNotificationNav('unread')}
                    >
                      Unread
                    </span>
                    <span
                      className={`cursor-pointer ${
                        notificationNav === 'read' && styles['active-nav']
                      }`}
                      onClick={() => setNotificationNav('read')}
                    >
                      Read
                    </span>
                  </nav>
                  <p
                    className='text-xs hover:text-secondaryColor-normal hover:underline cursor-pointer'
                    onClick={() =>
                      updateReadNotificationAll.mutate({
                        user_id: currentAccount.id,
                      })
                    }
                  >
                    Mark all as read
                  </p>
                </div>
              </div>
              {fetchNotifications.isSuccess && (
                <div className='flex-1 overflow-y-auto no-scrollbar w-full'>
                  {notifications?.map(
                    (notification: IGetNotificationsServiceResData) => (
                      <Menu.Item key={notification.uuid}>
                        {({ active }) => {
                          return (
                            <div
                              className={classNames(
                                active ? 'bg-gray-50 border-opacity-100' : '',
                                `flex items-center justify-between gap-2 px-4 py-4 rounded-md text-sm  cursor-pointer ${
                                  notification.isRead
                                    ? 'text-neutralColor-normal'
                                    : 'text-neutralColor-medium'
                                }`
                              )}
                              onClick={() =>
                                notificationOpenHandler(notification)
                              }
                            >
                              <div className='flex flex-col gap-1'>
                                <div className='flex gap-1'>
                                  <span className='font-semibold'>
                                    {notification.content}
                                  </span>
                                  <span className='text-neutralColor-normal'>
                                    {getElapsedTime(
                                      new Date(
                                        notification.createdAt
                                      ).getTime() / 1000
                                    )}
                                  </span>
                                </div>
                                {notification?.meta?.resourceName && (
                                  <span className='hover:underline cursor-pointer'>
                                    {notification?.meta?.resourceName}
                                  </span>
                                )}
                                {notification?.meta?.projectName && (
                                  <span className='text-xs text-neutralColor-normal'>
                                    {notification?.meta?.projectName}
                                  </span>
                                )}
                              </div>
                              {!notification.isRead && (
                                <span className='bg-tertiaryColor-normal h-2 w-2 rounded-full'></span>
                              )}
                            </div>
                          );
                        }}
                      </Menu.Item>
                    )
                  )}
                  {notifications?.length === 0 && (
                    <div className='flex flex-col justify-start items-center  h-full mt-10'>
                      <SparklesIcon className='h-6 w-6 text-primaryColor-normal' />
                      <h4 className='py-4'>
                        You don't have any {notificationNav} notifications.
                      </h4>
                    </div>
                  )}
                </div>
              )}
              {!fetchNotifications.isSuccess && !fetchNotifications.isLoading && (
                <div className='pt-14 w-9/12'>
                  <Error
                    icon
                    display='col'
                    errorMsg='Uh oh! There was an error to get your notifications. Please try again later.'
                  />
                </div>
              )}
              {fetchNotifications.isLoading && (
                <div className='animate-pulse w-full space-y-2'>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                  <div className='w-full h-16 bg-neutralColor-lighter rounded-md'></div>
                </div>
              )}
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </>
  );
}
